import React from 'react';
import Color from 'color';
import classNames from 'classnames';
import { isNullOrUndefined, isNullOrUndefinedOrEmpty } from 'rewire-common';
import { Observe } from 'rewire-core';
import { IGridFontSizes, ICell, IGridColors, CellModel } from 'rewire-grid';
import { WithStyle } from 'rewire-ui';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import Popover, { PopoverOrigin } from '@material-ui/core/Popover';
import { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import Grid from '../ui/Grid';
import Loader from '../ui/Loader';
import SchedulingCandidateEmployeeGridHeader from './SchedulingCandidateEmployeeGridHeader';
import SchedulingCandidateEmployeeGridModel, { IShiftEntryText } from './SchedulingCandidateEmployeeGridModel';
import { withStyles } from '../utility/styles';

const employeeCellStyles = (_theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    padding: '4px',
  },
});

interface IScheduleEmployeeCellProps {
  model: SchedulingCandidateEmployeeGridModel;
  cell: CellModel;
  classes: any;
}

export const SchedulingEmployeeCell: React.FunctionComponent<{ cell: ICell }> = React.memo(
  withStyles<IScheduleEmployeeCellProps>(employeeCellStyles, (props: IScheduleEmployeeCellProps) => {
    const { cell, classes } = props;
    let value = cell.column.map?.(cell.value) as string | JSX.Element;
    value = !isNullOrUndefinedOrEmpty(value) ? value : <span>&nbsp;</span>;

    return (
      <div className={classes.root} style={{ textAlign: cell.align }}>
        {value}
      </div>
    );
  }),
  () => false,
);

const shiftCellStyles = (theme: Theme) => ({
  root: {
    lineHeight: '1.5em',
    width: '100%',
    height: '100%',
    padding: '4px',
  },
  available: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  workingNonIntersectingTimes: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  notAvailable: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  shiftEntryContainer: {
    lineHeight: '1.25em',
  },
  entrySpacing: {
    paddingTop: '10px',
  },
  showMoreButton: {
    cursor: 'pointer',
    '&:hover, &:active': {
      color: Color(theme.palette.dialogPrimary.main).lighten(0.35).string(),
    },
  },
  showMoreButtonAvailable: {
    color: '#d0def5',
    '&:hover, &:active': {
      color: Color('#d0def5').lighten(0.07).string(),
    },
  },
  showMoreButtonWorkingNonIntersectingTimes: {
    color: Color('#d0def5').darken(0.5).string(),
    '&:hover, &:active': {
      color: Color('#d0def5').darken(0.35).string(),
    },
  },
  showMoreButtonNotAvailable: {
    color: '#d0def5',
    '&:hover, &:active': {
      color: Color('#d0def5').lighten(0.07).string(),
    },
  },
});

export const getSchedulingShiftCellRenderer = (model: any) =>
  React.memo(
    withStyles(shiftCellStyles, (props: IScheduleEmployeeCellProps) => {
      const { cell, classes } = props;
      const shiftEntriesText = cell.value?.shiftEntriesText ?? [];
      const firstShiftEntryText = shiftEntriesText[0];
      const availability = cell.value.availability;
      let availabilityClass = undefined;
      let availabilityShowMoreButtonClass = undefined;
      switch (availability) {
        case 'Available':
          availabilityClass = classes.available;
          availabilityShowMoreButtonClass = classes.showMoreButtonAvailable;
          break;
        case 'WorkingNonIntersectingTimes':
          availabilityClass = classes.workingNonIntersectingTimes;
          availabilityShowMoreButtonClass = classes.showMoreButtonWorkingNonIntersectingTimes;
          break;
        case 'NotAvailable':
          availabilityClass = classes.notAvailable;
          availabilityShowMoreButtonClass = classes.showMoreButtonNotAvailable;
          break;
      }
      return (
        <div className={classNames(classes.root, availabilityClass)} style={{ textAlign: cell.align }}>
          {firstShiftEntryText && (
            <div>
              <div className={classes.shiftEntryContainer}>{firstShiftEntryText.effective}</div>
              <div className={classes.shiftEntryContainer}>{`(${firstShiftEntryText.units} ${firstShiftEntryText.reason})`}</div>
              <div className={classes.shiftEntryContainer}>{firstShiftEntryText.occupation}</div>
              {shiftEntriesText.length > 1 && (
                <div className={classes.shiftEntryContainer}>
                  <Link
                    onClick={(evt: any) => {
                      model.showSchedulingShiftCellDataPopup(cell);
                      evt.stopPropagation();
                    }}
                    onFocus={(evt: any) => {
                      evt.stopPropagation();
                    }}
                    onMouseDown={(evt: any) => {
                      evt.stopPropagation();
                      evt.preventDefault();
                    }}
                    className={classNames(classes.showMoreButton, availabilityShowMoreButtonClass)}
                    variant="caption"
                    underline="always"
                  >
                    {`${shiftEntriesText.length - 1} More`}
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }),
    () => false,
  );

const shiftCellDataPopupStyles = (_theme: Theme) => ({
  root: {
    lineHeight: '1.5em',
    padding: '15px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: '10px',
  },
  date: {},
  employee: {
    fontWeight: 500,
  },
  closeIcon: {
    marginLeft: '30px',
  },
  shiftEntryContainer: {
    lineHeight: '1.25em',
  },
  entrySpacing: {
    paddingTop: '10px',
  },
});

export const SchedulingShiftCellDataPopup: React.FunctionComponent<any> = React.memo(
  withStyles(shiftCellDataPopupStyles, (props: any) => {
    return (
      <Observe
        render={() => {
          const { model, classes } = props;
          const cell = model.schedulingShiftCell as CellModel;
          const cellData = cell?.value;
          if (!cellData) {
            return null;
          }
          const date = cellData && model.toDisplayDateString(cellData.earnedDate);
          const employee = cellData.details?.[0]?.employee?.name ?? '';
          const shiftEntriesText = cellData.shiftEntriesText ?? [];
          const anchorOrigin: PopoverOrigin = { vertical: 'bottom', horizontal: 'right' };
          const transformOrigin: PopoverOrigin = { vertical: 'top', horizontal: 'right' };

          return (
            <Popover
              classes={{ paper: classes.root }}
              open={model.schedulingShiftCellDataPopupIsOpen}
              onClose={model.hideSchedulingShiftCellDataPopup}
              anchorOrigin={anchorOrigin}
              transformOrigin={transformOrigin}
              getContentAnchorEl={null}
              anchorEl={cell?.element}
              marginThreshold={5}
            >
              <div className={classes.header}>
                <div className={classes.date}>
                  <Typography variant="body2">
                    {`${date} - `}
                    <span className={classes.employee}>{employee}</span>
                  </Typography>
                </div>
                <IconButton className={classes.closeIcon} onClick={model.hideSchedulingShiftCellDataPopup}>
                  <CloseIcon />
                </IconButton>
              </div>
              {shiftEntriesText.map((shiftEntry: IShiftEntryText, idx: number) => (
                <div key={idx.toString()} className={idx > 0 ? classes.entrySpacing : undefined}>
                  <div className={classes.shiftEntryContainer}>
                    <Typography variant="body2">{`${shiftEntry.effective} - ${shiftEntry.units} - ${shiftEntry.reason}`}</Typography>
                  </div>
                  <div className={classes.shiftEntryContainer}>
                    <Typography variant="body2">{shiftEntry.occupation}</Typography>
                  </div>
                </div>
              ))}
            </Popover>
          );
        }}
      />
    );
  }),
  () => false,
);

const styles = (_theme: Theme) => ({
  gridContainer: {
    margin: '0px',
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  cellInnerContainer: {
    padding: '0px',
  },
});

interface ISchedulingCandidateEmployeeGridProps {
  model: SchedulingCandidateEmployeeGridModel;
  classes?: any;
  GridHeading?: () => JSX.Element | null;
}

type SchedulingCandidateEmployeeGridProps = WithStyle<ReturnType<typeof styles>, ISchedulingCandidateEmployeeGridProps>;

class SchedulingCandidateEmployeeGrid extends React.Component<SchedulingCandidateEmployeeGridProps> {
  gridFontSizes: IGridFontSizes;
  gridColors: IGridColors;

  constructor(props: SchedulingCandidateEmployeeGridProps) {
    super(props);

    this.gridFontSizes = {
      header: '0.75em',
      body: '0.75em',
      groupRow: '1em',
      toggleMenu: '0.8em',
    };

    this.gridColors = {
      // cellSelectedBackground: '#D0DEE8',
      // cellOutline: 'none',
    };
  }

  renderSchedulingGrid = React.memo((): JSX.Element => {
    const { model, classes } = this.props;
    return (
      <Observe
        render={() => (
          <>
            <Loader loading={model.isLoading}>
              <Observe
                render={() =>
                  !model.scheduleRuleContext ? (
                    <Typography>No Schedule Rule Shift Selected</Typography>
                  ) : model.hasSchedulingResults ? (
                    !isNullOrUndefined(model.schedulingGrid.id) ? (
                      <>
                        <Grid
                          key={model.schedulingGrid.id}
                          grid={model.schedulingGrid}
                          cellClasses={{ cellInnerContainer: classes.cellInnerContainer }}
                          gridFontSizes={this.gridFontSizes}
                          gridColors={this.gridColors}
                        />
                        <Observe render={() => <SchedulingShiftCellDataPopup model={model} />} />
                      </>
                    ) : null
                  ) : (
                    <Typography>No Results</Typography>
                  )
                }
              />
            </Loader>
          </>
        )}
      />
    );
  });

  render() {
    const { model, classes, GridHeading } = this.props;

    return (
      <Observe
        render={() => (
          <div className={classes.gridContainer}>
            <SchedulingCandidateEmployeeGridHeader model={model} />
            {GridHeading && <GridHeading />}
            <this.renderSchedulingGrid />
          </div>
        )}
      />
    );
  }
}

export default withStyles<ISchedulingCandidateEmployeeGridProps>(styles, SchedulingCandidateEmployeeGrid);
